import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import type { DocViewMode } from './index';

type SummaryMemoToggleProps = {
  viewMode: DocViewMode;
  onChangeViewMode?: (mode: DocViewMode) => void;
  /** 메모 탭 노출 여부 — 작성자는 항상, 비작성자(공유 문서)는 메모 내용이 있을 때만 */
  showMemoTab?: boolean;
  /** AI 정리(content)가 비어있어 탭 비활성 — 메모만 작성 가능 */
  contentDisabled?: boolean;
  /** AI 정리 생성 중 — 'AI 정리' 아이콘을 정적 이미지 대신 동영상(AI 아바타)으로 렌더 */
  isOrganizing?: boolean;
};

/**
 * 본문 좌측 상단 "AI 정리 / 메모" 세그먼트 토글.
 *
 * - AI 정리: 기존 본문(회의록 내용)을 표시
 * - 메모: 별도 메모 본문을 표시 (동일한 에디터, 별도 textarea)
 *   → 작성자(owner)는 항상 메모 탭 노출. 비작성자(공유 문서)는 메모 내용이
 *     있을 때만 노출하여 읽기 전용으로 조회 가능(showMemoTab).
 *
 * 선택 표시는 흰색 배경을 각 버튼에 직접 칠하지 않고, 선택된 항목 뒤로
 * 미끄러지는 pill(흰 배경) 하나를 두어 left/width 트랜지션으로 이동시킨다.
 */
const SummaryMemoToggle = ({
  viewMode,
  onChangeViewMode,
  showMemoTab,
  contentDisabled,
  isOrganizing,
}: SummaryMemoToggleProps) => {
  const { t } = useTranslation();

  const segmentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLButtonElement>(null);
  const memoRef = useRef<HTMLButtonElement>(null);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(
    null,
  );

  const measure = useCallback(() => {
    const target =
      viewMode === 'memo' && showMemoTab ? memoRef.current : contentRef.current;
    if (!target) return;
    setPill({ left: target.offsetLeft, width: target.offsetWidth });
  }, [viewMode, showMemoTab]);

  // 선택 변경 시(그리고 초기 마운트 시) pill 위치 계산.
  // useLayoutEffect → paint 전에 위치가 잡히므로 초기 진입에서 슬라이드가 튀지 않는다.
  useLayoutEffect(() => {
    measure();
  }, [measure, contentDisabled]);

  // 폰트 로드/리사이즈 등으로 버튼 폭이 바뀌면 pill 재계산.
  useLayoutEffect(() => {
    const el = segmentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <Segment ref={segmentRef}>
      {pill && <Pill style={{ left: pill.left, width: pill.width }} />}
      <SegmentItem
        ref={contentRef}
        type="button"
        selected={viewMode === 'content'}
        disabled={contentDisabled}
        onClick={() => !contentDisabled && onChangeViewMode?.('content')}
      >
        {t('AI 정리')}
        {isOrganizing && <Spinner aria-hidden />}
      </SegmentItem>
      {showMemoTab && (
        <SegmentItem
          ref={memoRef}
          type="button"
          selected={viewMode === 'memo'}
          onClick={() => onChangeViewMode?.('memo')}
        >
          {t('메모')}
        </SegmentItem>
      )}
    </Segment>
  );
};

export default SummaryMemoToggle;

/** AI 정리 생성 중 'AI 정리' 탭 라벨 뒤에 표시하는 로딩 스피너 */
const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(199, 122, 196, 0.25);
  border-top-color: #c77ac4;
  animation: ${spin} 0.7s linear infinite;
`;

const Segment = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  background-color: var(--dk-bg-muted, #f3f5f6);
`;

/** 선택된 항목 뒤에서 미끄러지는 흰색 pill */
const Pill = styled.span`
  position: absolute;
  top: 4px;
  bottom: 4px;
  border-radius: 4px;
  background-color: var(--dk-bg, #ffffff);
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.08);
  pointer-events: none;
  z-index: 0;
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SegmentItem = styled.button<{ selected: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border-radius: 4px;
  font-size: 16px;
  line-height: 24px;
  white-space: nowrap;
  cursor: pointer;
  background-color: transparent;
  color: ${({ selected }) => (selected ? 'var(--dk-text-strong, #4d5157)' : 'var(--dk-text-secondary, #888d96)')};
  transition: color 0.2s ease;

  &:disabled {
    cursor: default;
    color: var(--dk-text-dim, #c4c8ce);
    background-color: transparent;
    box-shadow: none;
  }
`;
